import React, { useEffect, useState } from 'react';
import {
    Typography,
    Row,
    Col,
    Button,
    Table,
    Progress,
    message,
    Divider,
    Collapse,
    Upload,
    Icon,
    Modal
} from "antd";
import { platformApi } from '../../../../../../../api';
import empty from '../../../../../../../assets/empty.png';
import { ENDPOINT } from '../../../../../../../constants';
import VehicleName from '../../../../../../VehicleName';

const { Text } = Typography;
const { Panel } = Collapse;

const getFileIcon = (url) => {
    const extension = url.split(".").pop().toLowerCase();
    switch (extension) {
        case "pdf":
            return (
                <Icon
                    type="file-pdf"
                    style={{ fontSize: "32px", color: "#ff4d4f" }}
                />
            );
        case "doc":
        case "docx":
            return (
                <Icon
                    type="file-word"
                    style={{ fontSize: "32px", color: "#1890ff" }}
                />
            );
        case "xls":
        case "xlsx":
            return (
                <Icon
                    type="file-excel"
                    style={{ fontSize: "32px", color: "#52c41a" }}
                />
            );
        case "mp4":
        case "webm":
        case "ogg":
        case "mov":
            return (
                <Icon
                    type="video-camera"
                    style={{ fontSize: "32px", color: "#1890ff" }}
                />
            );
        case "jpg":
        case "jpeg":
        case "png":
        case "gif":
            return (
                <img
                    src={url}
                    alt="Complaint preview"
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                    }}
                />
            );
        default:
            return <Icon type="file" style={{ fontSize: "32px" }} />;
    }
};

const MechanicView = ({ id }) => {
    const [data, setData] = useState(null);
    const [completionPercentage, setCompletionPercentage] = useState(0);
    const [uploadingStates, setUploadingStates] = useState({});
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState("");

    useEffect(() => {
        fetchJobOrderData();
    }, [id]);

    const handleCancel = () => setPreviewVisible(false);

    const fetchJobOrderData = async () => {
        try {
            const response = await platformApi.get(`/api/jobOrder/${id}`);
            if (response.data.code === 200) {
                const { response: { data } } = response.data;
                setData(data);
                calculateCompletionPercentage(data.complaint || []);
            } else {
                message.error('Failed to fetch job order data');
            }
        } catch (error) {
            console.error('Error fetching job order:', error);
            message.error('Failed to fetch job order data');
        }
    };

    const calculateCompletionPercentage = (complaints) => {
        if (!complaints || complaints.length === 0) {
            setCompletionPercentage(0);
            return;
        }
        const completed = complaints.filter(c => c.jobStatus === 'COMPLETED').length;
        const percentage = (completed / complaints.length) * 80; // 80% max as per requirement
        setCompletionPercentage(Math.round(percentage));
    };

    const handleUploadChange = async (info, complaint) => {
        const { file } = info;

        if (file.status === "uploading") {
            setUploadingStates(prev => ({ ...prev, [complaint.id]: true }));
            return;
        }

        if (file.status === "done" || file.status === "error") {
            setUploadingStates(prev => ({ ...prev, [complaint.id]: false }));

            if (file.status === "error") {
                message.error("Unable to upload file");
                return;
            }

            const originFileObj = file.originFileObj;
            if (!originFileObj) return;

            const formData = new FormData();
            formData.append("profile", originFileObj);
            formData.append("master", "Transaction Master");
            formData.append("module", "Job Order");
            formData.append("id", id || "");

            try {
                const res = await platformApi.post("/api/upload/image", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                if (res.data.code === 200) {
                    const newUrl = res.data.response.data.Location;
                    const existingImages = complaint.images || [];
                    const updatedImages = [...existingImages, { url: newUrl }];

                    const updateRes = await platformApi.put(`/api/jobOrder/complaint/${complaint.id}/images`, {
                        images: updatedImages
                    });

                    if (updateRes.data.code === 200) {
                        message.success("File uploaded successfully");
                        fetchJobOrderData();
                    } else {
                        message.error("Failed to save file reference");
                    }
                } else {
                    message.error("Upload failed");
                }
            } catch (err) {
                console.error("Error uploading file:", err);
                message.error("Unable to upload file");
            }
        }
    };

    const handleDeleteImage = async (imgUrl, complaint) => {
        try {
            await platformApi.post("/api/upload/deleteFile", { url: imgUrl });

            const existingImages = complaint.images || [];
            const updatedImages = existingImages.filter(img => img.url !== imgUrl);

            const updateRes = await platformApi.put(`/api/jobOrder/complaint/${complaint.id}/images`, {
                images: updatedImages
            });

            if (updateRes.data.code === 200) {
                message.success("File deleted successfully");
                fetchJobOrderData();
            } else {
                message.error("Failed to delete file reference");
            }
        } catch (err) {
            console.error("Error deleting file:", err);
            message.error("Failed to delete file");
        }
    };

    const columns = [
        {
            title: "S.No",
            render: (d) => (
                <h3>{data.complaint.indexOf(d) + 1}</h3>
            )
        },
        {
            title: "Complaints",
            render: (record) => (
                <div>
                    <p>{record.complaint}</p>
                    <p style={{ color: record.jobStatus === 'COMPLETED' ? 'green' : 'orange' }}>
                        Status: {record.jobStatus === 'COMPLETED' ? 'Completed' : 'Pending'}
                    </p>
                </div>
            )
        },
        {
            title: "Photos / Videos",
            key: "attachment",
            render: (record) => {
                const imagesList = record.images || [];
                const isUploading = uploadingStates[record.id] || false;

                const uploadButton = (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        {isUploading ? (
                            <>
                                <Icon type="loading" style={{ fontSize: "18px", color: "#1890ff", marginBottom: "4px" }} />
                                <div className="ant-upload-text" style={{ color: "#1890ff", fontWeight: "500", fontSize: "10px" }}>Uploading...</div>
                            </>
                        ) : (
                            <>
                                <Icon type="plus" style={{ fontSize: "14px" }} />
                                <div className="ant-upload-text" style={{ fontSize: "10px" }}>Upload</div>
                            </>
                        )}
                    </div>
                );

                return (
                    <div className="UploadDocuments complaint-media-container" style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                        <style>{`
                            .complaint-media-container .image-preview-card,
                            .complaint-media-container .ant-upload.ant-upload-select-picture-card {
                                width: 70px !important;
                                height: 70px !important;
                                margin: 0 !important;
                                padding: 0 !important;
                            }
                            .complaint-media-container .image-preview-card .hover-overlay {
                                gap: 10px !important;
                            }
                            .complaint-media-container .image-preview-card .hover-overlay .action-btn {
                                width: 20px !important;
                                height: 20px !important;
                                font-size: 14px !important;
                                background: transparent !important;
                                background-color: transparent !important;
                                border: none !important;
                                display: flex !important;
                                align-items: center !important;
                                justify-content: center !important;
                                box-shadow: none !important;
                                border-radius: 0 !important;
                            }
                            .complaint-media-container .image-preview-card .hover-overlay .action-btn:hover {
                                background: transparent !important;
                                background-color: transparent !important;
                                color: #1890ff !important;
                                transform: scale(1.2) !important;
                            }
                            .complaint-media-container .image-preview-card .hover-overlay .action-btn.delete-btn:hover {
                                background: transparent !important;
                                background-color: transparent !important;
                                color: #ff4d4f !important;
                            }
                        `}</style>

                        {/* Thumbnails of uploaded images/media */}
                        {imagesList.map((img, index) => (
                            <div
                                key={index}
                                className="image-preview-card"
                            >
                                {getFileIcon(img.url)}
                                <div className="hover-overlay">
                                    <span
                                        className="action-btn view-btn"
                                        onClick={() => {
                                            const extension = img.url.split(".").pop().toLowerCase();
                                            if (["jpg", "jpeg", "png", "gif"].includes(extension)) {
                                                setPreviewImage(img.url);
                                                setPreviewVisible(true);
                                            } else {
                                                window.open(img.url, "_blank");
                                            }
                                        }}
                                    >
                                        <Icon type="eye" />
                                    </span>
                                    <span
                                        className="action-btn delete-btn"
                                        onClick={() => handleDeleteImage(img.url, record)}
                                    >
                                        <Icon type="delete" />
                                    </span>
                                </div>
                            </div>
                        ))}

                        {/* Upload component - Only show if no attachment has been uploaded yet */}
                        {imagesList.length === 0 && (
                            <Upload
                                action={`${ENDPOINT}api/upload/mocky`}
                                listType="picture-card"
                                showUploadList={false}
                                onChange={(info) => handleUploadChange(info, record)}
                                accept=".jpg,.png,.jpeg,.gif,.pdf,.doc,.docx,.mp4,.webm,.ogg,.mov"
                                disabled={isUploading}
                            >
                                {uploadButton}
                            </Upload>
                        )}
                    </div>
                );
            }
        },
        {
            title: "Action",
            render: (record) => (
                <Button
                    type="primary"
                    disabled={record.jobStatus === 'COMPLETED'}
                    onClick={() => markAsComplete(record)}
                >
                    Mark as Complete
                </Button>
            )
        }
    ];

    const markAsComplete = async (complaint) => {
        try {
            const response = await platformApi.put(`/api/jobOrder/complaint/${complaint.id}/status`, {
                status: 'COMPLETED'
            });

            if (response.data.code === 200) {
                message.success('Complaint marked as complete');
                // Refresh data
                fetchJobOrderData();
            } else {
                message.error('Failed to update complaint status');
            }
        } catch (error) {
            console.error('Error updating complaint status:', error);
            message.error('Failed to update complaint status');
        }
    };

    if (!data) {
        return <div>Loading...</div>;
    }

    return (
        <div className="MechanicView">
            <div style={{ backgroundColor: 'white', padding: '1rem', marginBottom: '1rem' }}>
                <h2 style={{ marginBottom: '1rem', color: 'red' }}>Vehicle Details</h2>
                <Row className="box">
                    <Col xs={24} md={6}>
                        <div>
                            <img
                                className="image"
                                src={data.vehicle && data.vehicle.color?.url || empty}
                                style={{ height: '180px', width: '220px', objectFit: 'contain' }}
                                alt="Vehicle"
                            />
                        </div>
                    </Col>
                    <Col xs={24} md={18}>
                        <h2><VehicleName nameOnly value={data.vehicle?.vehicle} /></h2>
                        <Row>
                            <Col xs={24} md={12}>
                                <table>
                                    <thead>
                                        <tr>
                                            <td style={{ fontWeight: 'bolder' }}>Customer Name</td>
                                            <td>:</td>
                                            <td style={{ textAlign: 'left', paddingLeft: '.5rem' }}>{data.customer && data.customer.name}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontWeight: 'bolder' }}>Register Number</td>
                                            <td>:</td>
                                            <td style={{ textAlign: 'left', paddingLeft: '.5rem' }}>{data.vehicle && data.vehicle.registerNo}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontWeight: 'bolder' }}>Service Advisor</td>
                                            <td>:</td>
                                            <td style={{ textAlign: 'left', paddingLeft: '.5rem' }}>-</td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontWeight: 'bolder' }}>Odometer Reading</td>
                                            <td>:</td>
                                            <td style={{ textAlign: 'left', paddingLeft: '.5rem' }}>{data && data.kms}</td>
                                        </tr>
                                    </thead>
                                </table>
                            </Col>
                            <Col xs={24} md={12}>
                                <table>
                                    <thead>
                                        <tr>
                                            <td style={{ fontWeight: 'bolder' }}>Job No</td>
                                            <td>:</td>
                                            <td style={{ textAlign: 'left', paddingLeft: '.5rem' }}>{data.jobNo}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontWeight: 'bolder' }}>Chassis No</td>
                                            <td>:</td>
                                            <td style={{ textAlign: 'left', paddingLeft: '.5rem' }}>{data.vehicle && data.vehicle.chassisNo}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontWeight: 'bolder' }}>Engine No</td>
                                            <td>:</td>
                                            <td style={{ textAlign: 'left', paddingLeft: '.5rem' }}>{data.vehicle && data.vehicle.engineNo}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontWeight: 'bolder' }}>Service Type</td>
                                            <td>:</td>
                                            <td style={{ textAlign: 'left', paddingLeft: '.5rem' }}>{data.serviceType}</td>
                                        </tr>
                                    </thead>
                                </table>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </div>

            <div style={{ marginBottom: '2rem' }}>
                <h3>Work Completion Status</h3>
                <Progress
                    percent={completionPercentage}
                    status={completionPercentage === 80 ? "success" : "active"}
                    format={percent => `${percent}%`}
                />
                <Text type="secondary">Note: Maximum completion is 80% as 20% is reserved for washing and final inspection</Text>
            </div>

            <Divider />

            <div>
                <h3>Complaints List</h3>
                <Table
                    pagination={false}
                    columns={columns}
                    dataSource={data.complaint || []}
                    scroll={{ x: 800 }}
                />
            </div>

            <Modal 
                visible={previewVisible} 
                footer={null} 
                onCancel={handleCancel}
                width={950}
                bodyStyle={{ padding: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <img alt="Preview" style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }} src={previewImage} />
            </Modal>
        </div>
    );
};

export default MechanicView; 